import { fs } from '@/theme/tokens';
import { useMemo } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

/**
 * Searchable picker for one uKnowva profile field.
 *
 * Shared by the onboarding wizard ("Match your uKnowva fields") and the
 * dashboard's employee field mapping, so both behave the same. The options
 * are the instance's own `getProfileFields` list plus the uKnowva account
 * fields — often 100+ entries — which a plain select made you scroll through
 * by code. Typing here matches the field's name, its code and its type.
 *
 * A field `getUsers` does not export (`exported: false`) is listed but cannot
 * be picked: mapping it would import blanks for every employee, and the
 * server refuses it anyway. It stays visible so "where is my field?" has an
 * answer instead of a missing row.
 */

export interface HrmsFieldOption {
  code: string;
  label: string;
  type: string;
  /** false when uKnowva's employee export (getUsers) never sends it */
  exported?: boolean;
  /** why a value read from a uKnowva plugin or table cannot be picked */
  reason?: string;
}

const filter = createFilterOptions<HrmsFieldOption>({
  stringify: (o) => `${o.label} ${o.code} ${o.type}`,
  limit: 200 });

function display(o: HrmsFieldOption): string {
  return o.label && o.label !== o.code ? `${o.label} (${o.code})` : o.code;
}

export function HrmsFieldPicker({
  value,
  options,
  onChange,
  required = false,
  error = false,
  disabled = false,
  takenBy }: {
  value: string;
  options: HrmsFieldOption[];
  onChange: (code: string) => void;
  required?: boolean;
  error?: boolean;
  disabled?: boolean;
  /** code → the payroll fields already reading it, shown as a hint (one field may feed several) */
  takenBy?: Record<string, string>;
}) {
  // Pickable fields first, then by name — the list arrives sorted by code.
  const sorted = useMemo(
    () =>
      [...options].sort(
        (a, b) =>
          Number(a.exported === false) - Number(b.exported === false) ||
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
      ),
    [options],
  );

  // A saved code the instance no longer lists (renamed or removed in uKnowva)
  // is still shown as the value, so nobody loses sight of what was mapped.
  const selected = useMemo<HrmsFieldOption | null>(() => {
    if (!value) return null;
    return options.find((o) => o.code === value) ?? { code: value, label: value, type: '' };
  }, [options, value]);

  return (
    <Autocomplete
      size="small"
      fullWidth
      autoHighlight
      openOnFocus
      disabled={disabled}
      options={sorted}
      value={selected}
      onChange={(_, opt) => onChange(opt?.code ?? '')}
      filterOptions={filter}
      getOptionLabel={display}
      isOptionEqualToValue={(o, v) => o.code === v.code}
      getOptionDisabled={(o) => o.exported === false}
      noOptionsText="No uKnowva field matches. Try part of its name or code"
      slotProps={{ listbox: { style: { maxHeight: 320 } } }}
      renderOption={(props, o) => {
        const { key, ...rest } = props as typeof props & { key: string };
        const usedFor = takenBy?.[o.code];
        return (
          <Box component="li" key={key} {...rest} sx={{ display: 'block !important' }}>
            <Box sx={{ fontSize: fs.base, fontWeight: 600 }}>{o.label}</Box>
            <Box sx={{ fontSize: fs.tiny, color: 'text.secondary' }}>
              {o.code}
              {o.type ? ` · ${o.type}` : ''}
              {o.exported === false && ` · ${o.reason ?? 'not sent by uKnowva’s employee export'}`}
              {usedFor && o.code !== value && ` · also used for ${usedFor}`}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          placeholder={required ? 'Search and pick a field' : 'Not imported. Search to pick one'}
        />
      )}
    />
  );
}
